/**
 * Deploy stage versus resource environment: the rules, with no environment
 * access (PE-2046).
 *
 * A deployment carries two values. The deploy stage (`dev`, `pre`,
 * `stg`, `prod`) names what is deployed: stacks, Lambdas, the API
 * base-path suffix, the frontend hostname. The resource environment (`dev`,
 * `prod`) names what the code touches: tables, Cognito pools, buckets,
 * secrets, third-party credentials. `pre` (prerelease) is release-branch
 * code on dev resources; a future `stg` (staging) is release-tip code on prod
 * resources.
 *
 * Stage ids are short because AWS caps a Lambda function name at 64
 * characters and the longest `<service>-<stage>-<function>` stem already uses
 * 58 of them; `deployStage.lambdaNames.test.ts` fails on any name that would
 * overflow for any stage in `DEPLOY_STAGES`.
 *
 * This module holds the pure rules so the backend (`deployStage.ts`, reading
 * `process.env`) and both web apps (reading `import.meta.env`) resolve them
 * identically. It must stay free of `process` and `import.meta`.
 */

export type ResourceEnvironment = 'dev' | 'prod';

/** Every deploy stage a backend or build may name, in the order they appear in docs. */
export const DEPLOY_STAGES = ['dev', 'pre', 'stg', 'prod'] as const;
export type DeployStage = (typeof DEPLOY_STAGES)[number];

/** AWS Lambda rejects a function name longer than this. */
export const MAX_LAMBDA_FUNCTION_NAME_LENGTH = 64;

export function isResourceEnvironment(
  value: unknown,
): value is ResourceEnvironment {
  return value === 'dev' || value === 'prod';
}

/**
 * The API Gateway base path for a service on a deploy stage: the bare service
 * name for `prod`, otherwise the service name followed by the stage
 * (`aaaservicesdev`, `aaaservicespre`). The edge-mapping script derives
 * the same path from the REST API name.
 */
export function apiBasePath(service: string, stage: string): string {
  return stage === 'prod' ? service : `${service}${stage}`;
}

/**
 * Sentry environment for a deploy stage. `dev` and `prod` keep their
 * historical names; any other stage reports under its own name so prerelease
 * errors are neither filed under development (and skipped) nor production.
 */
export function sentryEnvironmentForStage(stage: string): string {
  if (stage === 'dev') return 'development';
  if (stage === 'prod') return 'production';
  return stage;
}

/**
 * The build-time variables a web app bundle can carry. `REACT_DEPLOY_STAGE`
 * and `REACT_RESOURCE_ENVIRONMENT` are the pair the build scripts export;
 * the others are the pre-PE-2046 switches, still honoured so a build made by
 * an older script or an external builder behaves exactly as before.
 */
export interface BuildEnvironment {
  REACT_DEPLOY_STAGE?: string;
  REACT_RESOURCE_ENVIRONMENT?: string;
  /** Forces the prod stage and prod resources, as it always has. */
  REACT_TEST_PROD_API?: string | boolean;
  /** mypeekaville-web's spelling of REACT_TEST_PROD_API. */
  REACT_APP_TEST_PROD_API?: string | boolean;
  /** Legacy dev switch (apps/frontend). */
  REACT_USE_DEV_API?: string | boolean;
  /** Legacy dev switch (apps/mypeekaville-web). */
  REACT_APP_USE_DEV_API?: string | boolean;
  /** Vite's mode; `development` has always meant the dev backend. */
  MODE?: string;
}

export interface BuildTarget {
  deployStage: string;
  resourceEnvironment: ResourceEnvironment;
}

function isSet(value: string | boolean | undefined): boolean {
  return value !== undefined && value !== false && value !== '';
}

/**
 * Resolve a web app build's deploy stage and resource environment.
 *
 * Precedence:
 *   1. `REACT_TEST_PROD_API` (either spelling): prod on prod, unchanged.
 *   2. `REACT_DEPLOY_STAGE`, with `REACT_RESOURCE_ENVIRONMENT`; when the
 *      latter is absent and the stage is itself `dev` or `prod`, the stage
 *      names the resources too. Any other stage without a resource
 *      environment is a misconfigured build and throws.
 *   3. Otherwise the legacy rule: Vite `development` mode or a legacy dev
 *      switch means dev on dev; anything else is prod on prod.
 */
export function resolveBuildTarget(env: BuildEnvironment): BuildTarget {
  if (isSet(env.REACT_TEST_PROD_API) || isSet(env.REACT_APP_TEST_PROD_API)) {
    return { deployStage: 'prod', resourceEnvironment: 'prod' };
  }

  if (isSet(env.REACT_DEPLOY_STAGE)) {
    const deployStage = env.REACT_DEPLOY_STAGE as string;
    const explicit = env.REACT_RESOURCE_ENVIRONMENT;
    if (isSet(explicit)) {
      if (!isResourceEnvironment(explicit)) {
        throw new Error(
          `REACT_RESOURCE_ENVIRONMENT must be "dev" or "prod", got "${explicit}"`,
        );
      }
      return { deployStage, resourceEnvironment: explicit };
    }
    if (isResourceEnvironment(deployStage)) {
      return { deployStage, resourceEnvironment: deployStage };
    }
    throw new Error(
      `REACT_RESOURCE_ENVIRONMENT is not set and REACT_DEPLOY_STAGE "${deployStage}" is not a resource environment`,
    );
  }

  const legacyDev =
    env.MODE === 'development' ||
    isSet(env.REACT_USE_DEV_API) ||
    isSet(env.REACT_APP_USE_DEV_API);
  return legacyDev
    ? { deployStage: 'dev', resourceEnvironment: 'dev' }
    : { deployStage: 'prod', resourceEnvironment: 'prod' };
}
